import React, { useEffect, useState } from 'react';
import { MapPin, Calendar, Briefcase, GitBranch, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Experiences = () => {
    const { t, language } = useLanguage();
    const [experiences, setExperiences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedExp, setExpandedExp] = useState({});

    const toggleExpand = (expId) => {
        setExpandedExp(prev => ({ ...prev, [expId]: !prev[expId] }));
    };

    useEffect(() => {
        const fetchExperiences = async () => {
            try {
                const response = await axios.get(`${API}/experiences/public`);
                // Sort by date_debut descending
                const sorted = response.data.sort((a, b) => {
                    const dateA = new Date(a.date_debut);
                    const dateB = new Date(b.date_debut);
                    return dateB - dateA;
                });
                setExperiences(sorted);
            } catch (error) {
                console.error('Error fetching experiences:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchExperiences();
    }, []);

    const formatDate = (dateStr) => {
        if (!dateStr) return t('Présent', 'Present');
        const [year, month] = dateStr.split('-');
        const months = language === 'fr' 
            ? ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
            : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[parseInt(month) - 1]} ${year}`;
    };

    const isInternational = (location) => {
        const internationalKeywords = ['Belgique', 'Belgium', 'Bruxelles', 'Brussels'];
        return internationalKeywords.some(kw => location.toLowerCase().includes(kw.toLowerCase()));
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <main data-testid="experiences-page" className="min-h-screen pt-24">
            {/* Header */}
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="max-w-3xl">
                        <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
                            {t('Expériences Professionnelles', 'Professional Experience')}
                        </h1>
                        <p className="text-lg text-slate-400">
                            {t(
                                'Plus de 15 ans d\'expérience en tant que Business Analyst et Développeur pour des grands comptes en France et à l\'international.',
                                'Over 15 years of experience as a Business Analyst and Developer for major companies in France and internationally.'
                            )}
                        </p>
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap gap-6 mt-8">
                        <div className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-blue-500"></span>
                            <span className="text-slate-400 text-sm">{t('Mission principale', 'Main mission')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-violet-500"></span>
                            <span className="text-slate-400 text-sm">{t('Mission parallèle', 'Parallel mission')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <MapPin size={16} className="text-cyan-400" />
                            <span className="text-slate-400 text-sm">International</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section className="pb-24" data-testid="timeline-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="relative border-l-2 border-blue-500/20 ml-6 md:ml-12 pl-8 md:pl-12 space-y-12">
                        {experiences.map((exp, index) => (
                            <div 
                                key={exp.id}
                                className={`relative fade-in`}
                                style={{ animationDelay: `${index * 0.1}s` }}
                                data-testid={`experience-${exp.id}`}
                            >
                                {/* Timeline dot */}
                                <div 
                                    className={`absolute w-4 h-4 rounded-full -left-[41px] md:-left-[57px] top-1 ${
                                        exp.type_mission === 'parallele' ? 'bg-violet-500' : 'bg-blue-500'
                                    }`}
                                    style={{ boxShadow: `0 0 0 4px rgba(${exp.type_mission === 'parallele' ? '139, 92, 246' : '59, 130, 246'}, 0.2)` }}
                                ></div>

                                {/* Card */}
                                <div className="glass-card p-6 md:p-8 card-hover">
                                    {/* Header */}
                                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                                        <div>
                                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                                {exp.type_mission === 'parallele' && (
                                                    <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/30">
                                                        <GitBranch size={12} />
                                                        {t('Parallèle', 'Parallel')}
                                                    </span>
                                                )}
                                                {isInternational(exp.localisation) && (
                                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                                                        International
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="text-xl md:text-2xl font-semibold text-white">
                                                {language === 'en' && exp.role_en ? exp.role_en : exp.role}
                                            </h3>
                                            <p className="text-lg text-blue-400 font-medium">{exp.client}</p>
                                        </div>
                                        <div className="text-right">
                                            <div className="flex items-center gap-2 text-white font-medium mb-1">
                                                <Calendar size={16} />
                                                <span>{formatDate(exp.date_debut)} — {formatDate(exp.date_fin)}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-white font-medium">
                                                <MapPin size={16} />
                                                <span>{exp.localisation}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <p className="text-white font-medium mb-6">
                                        {language === 'en' && exp.description_en ? exp.description_en : exp.description}
                                    </p>

                                    {/* Réalisations */}
                                    {((language === 'en' && exp.realisations_en?.length > 0) || exp.realisations?.length > 0) && (
                                        <div className="mb-6">
                                            <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                                                <Briefcase size={16} className="text-blue-400" />
                                                {t('Réalisations clés', 'Key achievements')}
                                            </h4>
                                            {(() => {
                                                const realisations = language === 'en' && exp.realisations_en?.length > 0 
                                                    ? exp.realisations_en 
                                                    : exp.realisations;
                                                const isExpanded = expandedExp[exp.id];
                                                const hasMore = realisations.length > 4;
                                                const displayedRealisations = isExpanded ? realisations : realisations.slice(0, 4);
                                                
                                                return (
                                                    <ul className="space-y-2">
                                                        {displayedRealisations.map((real, i) => (
                                                            <li key={i} className="flex items-start gap-2 text-white font-medium text-sm">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0"></span>
                                                                {real}
                                                            </li>
                                                        ))}
                                                        {hasMore && (
                                                            <li 
                                                                onClick={() => toggleExpand(exp.id)}
                                                                className="flex items-center gap-2 text-blue-400 text-sm cursor-pointer hover:text-blue-300 transition-colors font-medium"
                                                            >
                                                                {isExpanded ? (
                                                                    <>
                                                                        <ChevronUp size={16} />
                                                                        {t('Voir moins de détails...', 'See less details...')}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <ChevronDown size={16} />
                                                                        {t('Voir plus de détails...', 'See more details...')}
                                                                    </>
                                                                )}
                                                            </li>
                                                        )}
                                                    </ul>
                                                );
                                            })()}
                                        </div>
                                    )}

                                    {/* Context */}
                                    {exp.contexte && (
                                        <div className="mb-4">
                                            <span className="text-sm text-white font-medium">
                                                {t('Contexte', 'Context')}: {language === 'en' && exp.contexte_en ? exp.contexte_en : exp.contexte}
                                            </span>
                                        </div>
                                    )}

                                    {/* Technologies */}
                                    {exp.technos?.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {exp.technos.map((tech, i) => (
                                                <span key={i} className="tech-badge">{tech}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
};
